import { ENDPOINT_NAMES } from '../../../constants';
import type { ShowLogic, SurveyConfigBlock } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PutShowLogicArgs {
  surveyConfigId: string;
  id: string;
  showLogic: ShowLogic;
}

interface PutShowLogicContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: ShowLogic }>;
  getState: () => RootState;
}

/**
 * ### updateShowLogicInCache
 *
 * @description
 * Updates a specific show logic item in a draft array by its ID.
 * If the item doesn't exist, it will be added to the array (for creation case).
 *
 * @example
 * updateShowLogicInCache(draft, '123', updatedShowLogic);
 *
 * @param draft - The draft array containing show logic items
 * @param showLogicId - The ID of the show logic item to update
 * @param updatedShowLogic - The updated show logic data
 */
const updateShowLogicInCache = (
  draft: ShowLogic[] | undefined,
  showLogicId: string,
  updatedShowLogic: ShowLogic,
): void => {
  if (!draft) return;

  const index = draft.findIndex((item) => item.id === showLogicId);
  if (index !== -1) {
    // Update existing item
    draft[index] = { ...draft[index], ...updatedShowLogic };
  } else {
    // Add new item (creation case)
    draft.push(updatedShowLogic);
  }
};

/**
 * ### updateShowLogicIdInBlocks
 *
 * @description
 * Updates the showLogicId field in a survey config block's question.
 *
 * @example
 * updateShowLogicIdInBlocks(draft, 'question-123', 'showlogic-456');
 *
 * @param draft - The draft array containing survey config blocks
 * @param surveyConfigQuestionId - The ID of the survey config question
 * @param showLogicId - The show logic ID to set
 */
const updateShowLogicIdInBlocks = (
  draft: SurveyConfigBlock[] | undefined,
  surveyConfigQuestionId: string,
  showLogicId: string,
): void => {
  if (!draft) return;

  const block = draft.find((b) => b.surveyConfigQuestion?.id === surveyConfigQuestionId);
  if (block?.surveyConfigQuestion) {
    block.surveyConfigQuestion.showLogicId = showLogicId;
  }
};

/**
 * ### Put Show Logic Enhancer
 *
 * @description
 * Enhancer for putSurveyconfigsBySurveyConfigIdShowlogicsAndId endpoint.
 * Optimistically updates the show logic in the cache before the server responds.
 * If the request fails, the update is rolled back.
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const putSurveyconfigsBySurveyConfigIdShowlogicsAndId = (api: ProjectApi) => ({
  // Override invalidatesTags to prevent automatic refetching since we handle all cache updates optimistically
  invalidatesTags: () => [],
  async onQueryStarted(args: PutShowLogicArgs, context: PutShowLogicContext) {
    const { surveyConfigId, id, showLogic } = args;
    const { dispatch, queryFulfilled, getState } = context;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Optimistically update the show logics list cache
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_SURVEYCONFIGS_BY_SURVEY_CONFIG_ID_SHOWLOGICS,
      args: { surveyConfigId },
      updateFn: (draft: ShowLogic[]) => updateShowLogicInCache(draft, id, showLogic),
    });

    // Optimistically update the individual show logic by ID cache
    // Use upsertData (not upsert with updateFn) to create/update the cache entry
    // This prevents RTK Query from automatically refetching when the query becomes active
    // Note: RTK Query's upsertQueryData is designed to accept direct data for insertion/replacement.
    // While it technically accepts a union type (data | updateFn), using direct data is the
    // intended pattern for upsert operations. Using updateFn with non-existent cache entries
    // can cause the function to be stored in state instead of executed, leading to errors.
    updater.upsertData({
      endpointName: ENDPOINT_NAMES.GET_SURVEYCONFIGS_BY_SURVEY_CONFIG_ID_SHOWLOGICS_AND_ID,
      args: { surveyConfigId, id },
      data: showLogic,
    });

    // Optimistically update the blocks cache with the new showLogicId
    // Only update if blocks are actually cached (performance optimization)
    const surveyConfigQuestionId = showLogic.surveyConfigQuestionId;
    if (surveyConfigQuestionId) {
      const state = getState();
      const queries = state.projectApi?.queries;
      const matchingQueries = findMatchingCachedQueries<{
        projectId: number;
        surveyConfigId: string;
      }>({
        queries,
        endpointName:
          ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
        matcher: (args) => args.surveyConfigId === surveyConfigId,
      });

      // Only update blocks if queries exist (skip if not loaded yet)
      matchingQueries.forEach((args) => {
        updater.update({
          endpointName:
            ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
          args,
          updateFn: (draft: SurveyConfigBlock[]) =>
            updateShowLogicIdInBlocks(draft, surveyConfigQuestionId, id),
        });
      });
    }

    try {
      await queryFulfilled;
    } catch {
      // Rollback optimistic update on failure
      updater.rollback();
    }
  },
});

export default putSurveyconfigsBySurveyConfigIdShowlogicsAndId;
